import { CommonModule } from '@angular/common';
import { Component, Input, SimpleChanges, OnDestroy, OnInit } from '@angular/core';
import { DateRangePickerModule } from 'src/app/common/components/core/date-range-picker/date-range-picker.module';
import dayjs from 'dayjs/esm';
import { DropdownComponent } from 'src/app/common/components/core/dropdown/dropdown.component';
import { DropdownItem } from 'src/app/common/interfaces/dropdown-item.interface';
import { DisciplineNumber, DisciplineRelation } from 'src/app/common/enums/discipline.enum';
import { distinctUntilChanged, firstValueFrom, Subscription } from 'rxjs';
import { LookupClient } from 'src/app/common/services/lookup.client';
import { StorageService } from 'src/app/common/services/storage.service';
import { ButtonComponent, ButtonVariant } from "../../../../common/components/core/button/button.component";
import { format } from 'date-fns';
import { AlertFiltersEnum, DashboardTaskAlertTypes, FilterDateRange } from 'src/app/common/enums/Tasks.enum';
import { LandingClientService } from 'src/app/common/services/landing-page.client';
import { ViewTypeService } from 'src/app/services/view-type.service';
import { AlertFilters, ApplyTaskFilters, TherapistDropdownItem, TherapistDropdownResponse } from 'src/app/common/interfaces/home/tasks.interface';
import { LandingPageTaskService } from 'src/app/common/services/landingPage-task.service';
import { FiltersRequired } from 'src/app/common/interfaces/home/side-panel-details.interface';
import { AppConstants } from 'src/app/common/constants/app-constants';
import { ISODateFormat } from 'src/app/common/constants/core-constants';

@Component({
  selector: 'app-side-panel-filter',
  standalone: true,
  imports: [
    CommonModule,
    DateRangePickerModule,
    DropdownComponent,
    ButtonComponent
  ],
  templateUrl: './side-panel-filter.component.html',
  styleUrls: ['./side-panel-filter.component.scss']
})
export class SidePanelFilterComponent implements OnInit, OnDestroy {
  @Input() displayFilters!: FiltersRequired | undefined;
  @Input() alertType!: DashboardTaskAlertTypes | undefined;
  @Input() existingFilterData!: AlertFilters | undefined;

  dateRange: DropdownItem[] = [];
  disciplines: DropdownItem[] = [];
  patients: DropdownItem[] = [];
  therapists: TherapistDropdownResponse[] = [];
  displayTherapists: TherapistDropdownItem[] = [];
  physicians: DropdownItem[] = [];
  facilityId!: string;
  facilityIdSubscription!: Subscription;
  buttonVariant = ButtonVariant;
  alertFiltersEnum = AlertFiltersEnum;
  currentDate!: Date;
  ranges!: any;
  taskFilters!: ApplyTaskFilters;
  appConstants = AppConstants;  

  // Form properties
  dateRangeSelection!: string | undefined;
  fromDate!: string | undefined;
  endDate!: string | undefined;
  selectedDisciplines: string[] = [];
  selectedPatients: number[] = [];
  selectedTherapists: number[] = [];
  selectedPhysicians: number[] = [];

  isDataLoaded: boolean = false;

  constructor(
    private lookupClient: LookupClient,
    private storageService: StorageService,
    private landingPageClient: LandingClientService,
    private viewTypeService: ViewTypeService,
    private landingPageTaskService: LandingPageTaskService,
  ) {}

  async ngOnInit() {
    this.initializeDateRangeFilterOptions();
    this.disciplines = Object.keys(DisciplineNumber)
      .filter(key => isNaN(Number(key)))
      .map(key => ({
        id: DisciplineNumber[key as keyof typeof DisciplineNumber].toString(),
        name: key
      }));

    this.facilityIdSubscription = this.storageService.isSelectedFacility
      .pipe(distinctUntilChanged())
      .subscribe(async (item) => {
        this.isDataLoaded = false; 
        if(!!item){
          this.currentDate = new Date();
          this.ranges = {
            [this.appConstants.lastDay]: [dayjs(this.currentDate).subtract(1, 'day').startOf('day'), dayjs(this.currentDate).subtract(1, 'day').endOf('day')],
            [this.appConstants.lastThreeDays]: [dayjs(this.currentDate).subtract(3, 'day').startOf('day'), dayjs(this.currentDate).subtract(1, 'day').endOf('day')],
            [this.appConstants.lastOneWeek]: [dayjs(this.currentDate).subtract(7, 'day').startOf('day'), dayjs(this.currentDate).subtract(1, 'day').endOf('day')],
            [this.appConstants.lastFourteenDays]: [dayjs(this.currentDate).subtract(14, 'day').startOf('day'), dayjs(this.currentDate).subtract(1, 'day').endOf('day')],
            [this.appConstants.lastMonth]: [dayjs(this.currentDate).subtract(30, 'day').startOf('day'), dayjs(this.currentDate).subtract(1, 'day').endOf('day')],
          };          
          this.facilityId = item;
          // Resetting dropdown fields due to facility change
          this.resetFields();
          await this.initializeFilterOptions();
          this.initializeFilter();
          this.onDisciplineChange();
          this.isDataLoaded = true; 
        }
      });
  }

  resetFields() {
    this.patients = [];
    this.therapists = [];
    this.displayTherapists = [];
    this.physicians = [];
  }

  async ngOnChanges(changes: SimpleChanges) {
    if ((changes['existingFilterData'] && changes['existingFilterData'].currentValue) || (changes['displayFilters'] && changes['displayFilters'].currentValue)) {
      this.isDataLoaded = false; 
      this.initializeDateRangeFilterOptions();
      await this.initializeFilterOptions();
      this.initializeFilter();
      this.onDisciplineChange();
      this.isDataLoaded = true; 
    }
  }

  initializeDateRangeFilterOptions() {
    this.dateRange = Object.keys(FilterDateRange)
      .map(key => ({
        id: key,
        name: FilterDateRange[key as keyof typeof FilterDateRange].toString()
      }));
  }

  async initializeFilterOptions() {
    if (this.displayFilters?.isPatientFilter && this.patients.length === 0 && !!this.facilityId)
      this.patients = await firstValueFrom(this.lookupClient.getAllFacilityPatient(this.facilityId));

    if (this.displayFilters?.isTherapistFilter && this.therapists.length === 0 && !!this.facilityId) {
      var IsMissedtimesheet = (this.alertType && this.alertType === DashboardTaskAlertTypes.MissingTimeSheets) ? true : false;
      this.therapists = await firstValueFrom(this.lookupClient.getAllFacilityTherapistWithDiscipline(this.facilityId, IsMissedtimesheet));
      this.displayTherapists = this.therapists
        .map(item => ({
          id: item?.therapistId,
          name: `${item.lastName}, ${item.firstName}`,
          disciplines: item.disciplineIds
        }));
    }

    if (this.displayFilters?.isPhysicianFilter && this.physicians.length === 0 && !!this.facilityId)
      this.physicians = await firstValueFrom(this.lookupClient.getAllFacilityPhysician(this.facilityId));
  }

  initializeFilter() {
    const defaults = this.existingFilterData ?? {
      dateRange: this.dateRange[0]?.id ?? undefined,
      fromDate: undefined,
      endDate: undefined,
      disciplines: this.disciplines.map(item => Number(item.id)),
      patients: this.patients.map(item => Number(item.id)),
      therapists: this.therapists.map(item => Number(item.therapistId)),
      physicians: this.physicians.map(item => Number(item.id))
    };

    this.dateRangeSelection = defaults.dateRange ?? undefined;
    this.fromDate = defaults.fromDate ?? undefined;
    this.endDate = defaults.endDate ?? undefined;
    this.selectedDisciplines = this.displayFilters?.isDisciplineFilter ? defaults.disciplines?.map(item => item.toString()) ?? [] : [];
    this.selectedPatients = this.displayFilters?.isPatientFilter ? defaults.patients ?? [] : [];
    this.selectedTherapists = this.displayFilters?.isTherapistFilter ? defaults.therapists ?? [] : [];
    this.selectedPhysicians = this.displayFilters?.isPhysicianFilter ? defaults.physicians ?? [] : [];
  }

  onDateValueUpdate(selectedOption: any) {
    this.dateRangeSelection = selectedOption.value;
    const dateValues = [...this.ranges[FilterDateRange[selectedOption.value as keyof typeof FilterDateRange].toString()]];
    this.fromDate = dateValues[0] ? format(dateValues[0].toDate(), ISODateFormat) : undefined;
    this.endDate = dateValues[1] ? format(dateValues[1].toDate(), ISODateFormat) : undefined;
  }

  async applyFilter() {
    this.taskFilters = {
      facilityId: this.storageService.getSelectedFacilityId() ?? '',
      viewType: this.viewTypeService.getSelectedViewType(),
      alertType: this.alertType,
      filterAlerts: {
        dateRange: this.dateRangeSelection ?? undefined,
        fromDate: this.fromDate,
        endDate: this.endDate,
        disciplines: this.selectedDisciplines?.map(item => Number(item)) ?? [],
        patients: this.selectedPatients?.map(item => item) ?? [],
        therapists: this.selectedTherapists?.map(item => item) ?? [],
        physicians: this.selectedPhysicians?.map(item => item) ?? []
      }
    };

    const task = await firstValueFrom(this.landingPageClient.getFilteredAlertData(this.taskFilters));
    task.alertFilters = this.taskFilters.filterAlerts;
    this.landingPageTaskService.updateTasksDetailsValue(task, this.alertType ?? undefined);
  }

  onDisciplineChange() {
    if (this.displayFilters?.isTherapistFilter && this.displayFilters?.isDisciplineFilter) {
      this.displayTherapists = this.therapists.filter(therapist =>
        therapist?.disciplineIds?.some((item: any) =>
          this.selectedDisciplines.includes(item + '') ||
          this.selectedDisciplines.includes(DisciplineRelation[item] + '')
        )
      ).map(item => ({
        id: item?.therapistId,
        name: `${item.lastName}, ${item.firstName}`,
        disciplines: item.disciplineIds
      }));;
    }
  }

  ngOnDestroy() {
    this.facilityIdSubscription.unsubscribe();
  }
  
  onValueChange($event: any,selectedFilter: AlertFiltersEnum) {
    switch(selectedFilter){
      case AlertFiltersEnum.Discipline:
        this.selectedDisciplines = $event.value;
        break;
      case AlertFiltersEnum.Patient:
        this.selectedPatients = $event.value;
        break;
      case AlertFiltersEnum.Therapist:
        this.selectedTherapists = $event.value;
        break;
      case AlertFiltersEnum.Physician:
        this.selectedPhysicians = $event.value;
        break;
    }
  }

  isMultiSelect(options: number[] | string [] | DropdownItem[] | TherapistDropdownItem[]): boolean{
    return options.length > 1
  }
  
  isAutoSelectByDefault(options: number[] | string [] | DropdownItem[] | TherapistDropdownItem[], selectedOption: string[] | number[]): boolean{
    return !(options.length === 1 && selectedOption.length === 0)
  }
}
